import { SubmenuHandlers } from "../SubmenuHandlers";
import { ObjectBuilder, Image3D, Rectangle3D } from "@dreamcam/babylon-tools";
import { Env } from "../../../settings/Env";
import { Settings } from "../../../settings/Settings";
import { Container } from "@babylonjs/gui/2D/controls/container.js";
import { Control } from "@babylonjs/gui/2D/controls/control.js";
import { Vector2 } from "@babylonjs/core/Maths/math.vector.js";
import { text } from "../../../TextResources";
class VolumeSlider {
    constructor(_slider, _maxWidthPx, _volume = 1) {
        this._slider = _slider;
        this._maxWidthPx = _maxWidthPx;
        this._volume = _volume;
        this.volume = _volume;
    }
    get volume() {
        return this._volume;
    }
    set volume(volume) {
        if (volume < 0.00001)
            this._volume = 0;
        else if (volume > 0.99999)
            this._volume = 1;
        else
            this._volume = volume;
        this._slider.center.leftInPixels = this._slider.left.leftInPixels + this._slider.left.widthInPixels / 2;
        this._slider.center.widthInPixels = this._maxWidthPx * this._volume;
        this._slider.right.leftInPixels = this._slider.left.leftInPixels + this._slider.center.widthInPixels;
    }
    setVolumeBasedOnPosition(positionPx) {
        this.volume = positionPx / this._maxWidthPx;
    }
}
export class SubmenuVolume {
    constructor(mesh, callbacks, muteButtonCallback) {
        this._soundLevelPressed = false;
        this.refreshVolume = () => {
            if (this._volumeSlider) {
                this._volumeSlider.volume = this._callbacks.getVolume();
            }
        };
        this.mute = (muted) => {
            this._muteButtonCallback.update(muted);
            this._callbacks.setMuted(muted);
        };
        this._setSoundLevel = (control, eventData, eventState) => {
            if (this._volumeSlider &&
                eventData.x !== 0 &&
                eventData.y !== 0 &&
                eventState.target.name === eventState.currentTarget.name) {
                const xPositionPx = control.getLocalCoordinates(new Vector2(eventData.x)).x;
                this._volumeSlider.setVolumeBasedOnPosition(xPositionPx);
                this._callbacks.setVolume(this._volumeSlider.volume);
                this._callbacks.setMuted(false);
            }
        };
        this._callbacks = callbacks;
        this._muteButtonCallback = muteButtonCallback;
        this.submenuHandlers = new SubmenuHandlers(mesh);
        this.submenuHandlers.hideSubmenuInstantly();
        const menu = Env.broadcast.menu;
        const vol = menu.navigation.submenus.volume;
        const root = new Rectangle3D("volumeRoot");
        root.thickness = 0;
        root.background = menu.backgroundColor;
        root.cornerRadius = mesh.m2px(menu.cornerRadius);
        mesh.texture.addControl(root);
        const captionTextBlock = ObjectBuilder.createTextBlock3D(mesh, "volumeCaption", text.broadcast_menu_navigation_volume, vol.caption);
        captionTextBlock.verticalAlignment = Control.VERTICAL_ALIGNMENT_TOP;
        root.addControl(captionTextBlock);
        const wPx = mesh.m2px(vol.soundLevel.width);
        const hPx = mesh.m2px(vol.soundLevel.height);
        const lPx = mesh.m2px(vol.soundLevel.left);
        const soundVolume = Settings.getInstance().broadcast.soundVolume;
        const container = new Container("soundLevel");
        container.verticalAlignment = Control.VERTICAL_ALIGNMENT_TOP;
        container.heightInPixels = mesh.m2px(menu.navigation.height);
        root.addControl(container);
        container.onPointerDownObservable.add((eventData, eventState) => {
            if (eventData.buttonIndex === 0) {
                this._soundLevelPressed = true;
                this._setSoundLevel(bgImage, eventData, eventState);
            }
        });
        container.onPointerUpObservable.add((eventData, eventState) => {
            if (eventData.buttonIndex === 0) {
                this._soundLevelPressed = false;
                this._setSoundLevel(bgImage, eventData, eventState);
            }
        });
        container.onPointerEnterObservable.add(() => {
            this.submenuHandlers.hideSubmenuCancel();
        });
        container.onPointerOutObservable.add(() => {
            this.submenuHandlers.hideSubmenuStart(SubmenuHandlers.shortLifetime);
        });
        container.onPointerMoveObservable.add((eventData, eventState) => {
            if (this._soundLevelPressed) {
                this._setSoundLevel(bgImage, eventData, eventState);
            }
        });
        const bgImage = new Image3D("soundLevelBackground", vol.soundLevel.backgroundImage);
        bgImage.isHitTestVisible = false;
        bgImage.horizontalAlignment = Control.HORIZONTAL_ALIGNMENT_LEFT;
        bgImage.verticalAlignment = Control.VERTICAL_ALIGNMENT_TOP;
        bgImage.widthInPixels = wPx;
        bgImage.heightInPixels = hPx;
        bgImage.leftInPixels = lPx;
        bgImage.topInPixels = mesh.m2px(vol.soundLevel.top);
        container.addControl(bgImage);
        const fgLeftImage = new Image3D("soundLevelForegroundLeft", vol.soundLevel.foregroundImage);
        fgLeftImage.isHitTestVisible = false;
        fgLeftImage.horizontalAlignment = Control.HORIZONTAL_ALIGNMENT_LEFT;
        fgLeftImage.verticalAlignment = Control.VERTICAL_ALIGNMENT_TOP;
        fgLeftImage.widthInPixels = hPx;
        fgLeftImage.heightInPixels = hPx;
        fgLeftImage.leftInPixels = bgImage.leftInPixels;
        fgLeftImage.topInPixels = mesh.m2px(vol.soundLevel.top);
        container.addControl(fgLeftImage);
        const barMaxWidthPx = mesh.m2px(vol.soundLevel.width - vol.soundLevel.height);
        const fgBar = new Container("soundLevelForegroundBar");
        fgBar.isHitTestVisible = false;
        fgBar.horizontalAlignment = Control.HORIZONTAL_ALIGNMENT_LEFT;
        fgBar.verticalAlignment = Control.VERTICAL_ALIGNMENT_TOP;
        fgBar.widthInPixels = 0; //barMaxWidthPx * soundVolume;
        fgBar.heightInPixels = hPx;
        fgBar.leftInPixels = fgLeftImage.leftInPixels + fgLeftImage.widthInPixels;
        fgBar.topInPixels = mesh.m2px(vol.soundLevel.top);
        fgBar.background = vol.soundLevel.foregroundColor;
        container.addControl(fgBar);
        const fgRightImage = new Image3D("soundLevelForegroundRight", vol.soundLevel.foregroundImage);
        fgRightImage.isHitTestVisible = false;
        fgRightImage.horizontalAlignment = Control.HORIZONTAL_ALIGNMENT_LEFT;
        fgRightImage.verticalAlignment = Control.VERTICAL_ALIGNMENT_TOP;
        fgRightImage.widthInPixels = hPx;
        fgRightImage.heightInPixels = hPx;
        fgRightImage.leftInPixels = fgLeftImage.leftInPixels + fgLeftImage.widthInPixels + fgBar.widthInPixels;
        fgRightImage.topInPixels = mesh.m2px(vol.soundLevel.top);
        container.addControl(fgRightImage);
        this._volumeSlider = new VolumeSlider({ left: fgLeftImage, center: fgBar, right: fgRightImage }, barMaxWidthPx, soundVolume);
    }
}
